import { AssistantUITools } from "@repo/api/client";
import { Button, Flex, Icon, Text } from "@repo/ui";
import { ToolUIPart } from "ai";
import { useRef } from "react";
import {
	ConfirmToolCallArg,
	type ConfirmToolCallFn,
} from "../../../ai/useAssistantChat";
import { TextEditor, type TextEditorRef } from "../../editor/TextEditor";
import { ToolCallView } from "./ToolCallView";

type AssistantToolUIPart<ToolName extends keyof AssistantUITools> = ToolUIPart<
	Pick<AssistantUITools, ToolName>
>;

export const CallProviderToolCallView: React.FC<{
	part: AssistantToolUIPart<"call_provider">;
	isPendingConfirmation: boolean;
	confirmToolCall: ConfirmToolCallFn;
}> = ({ part, isPendingConfirmation, confirmToolCall }) => {
	// Handle pending confirmation state
	if (isPendingConfirmation) {
		if (part.state !== "input-available") return null;

		return (
			<CallProviderConfirmation
				phoneNumber={part.input?.phoneNumber}
				callReceiver={part.input?.callReceiver}
				query={part.input?.query}
				onConfirm={(arg: ConfirmToolCallArg<"call_provider">) =>
					confirmToolCall<"call_provider">(part.toolCallId, arg)
				}
			/>
		);
	}

	// For all other states (loading, output-available, output-error), use generic tool view
	return <ToolCallView part={part} />;
};

const CallProviderConfirmation: React.FC<
	AssistantUITools["call_provider"]["input"] & {
		onConfirm: (arg: ConfirmToolCallArg<"call_provider">) => void;
	}
> = ({ phoneNumber, callReceiver, query, onConfirm }) => {
	const queryInputRef = useRef<TextEditorRef>(null);

	const handleApprove = () => {
		const input: AssistantUITools["call_provider"]["input"] = {
			phoneNumber,
			callReceiver,
			query: queryInputRef.current?.getValue()?.trim() || query,
		};

		onConfirm({ permission: "approve", input });
	};

	const handleReject = () => {
		onConfirm({
			permission: "reject",
			output: {
				success: false,
				code: "REJECTED_BY_USER",
				message: "User rejected the assistant's request to call the provider",
			},
		});
	};

	return (
		<Flex
			column
			gap="md"
			padding="md"
			borderRadius="md"
			background="blue2"
			borderWidth="md"
			borderColor="blue6"
			flex
		>
			{/* Header */}
			<Flex align="flex-start" gap="xs">
				<Flex shrink={0} padding="xxxs">
					<Icon name="AlertCircle" size="sm" color="blue9" />
				</Flex>
				<Text size="md" color="blue12" weight="medium">
					Confirm provider phone call
				</Text>
			</Flex>

			{/* Call details */}
			<Flex column gap="xs">
				<Flex align="center" gap="xs">
					<Text size="md" color="blue11" weight="medium">
						Phone:
					</Text>
					<Text size="md" color="blue12">
						{phoneNumber}
					</Text>
				</Flex>
				{callReceiver && (
					<Flex align="center" gap="xs">
						<Text size="md" color="blue11" weight="medium">
							Call receiver:
						</Text>
						<Text size="md" color="blue12">
							{callReceiver}
						</Text>
					</Flex>
				)}

				<Text size="md" color="blue11" weight="medium">
					Query:
				</Text>
				<Flex
					column
					gap="xs"
					padding="xs"
					borderRadius="sm"
					background="background"
					borderWidth="md"
					borderColor="blue4"
				>
					<TextEditor
						ref={queryInputRef}
						placeholder="Edit query..."
						initialValue={query || ""}
					/>
				</Flex>
			</Flex>

			{/* Actions */}
			<Flex gap="xs" justify="space-between" align="center">
				<Button size="sm" variant="ghost" onPress={handleReject}>
					<Flex align="center" gap="xs">
						<Icon name="X" size="xs" />
						<Text size="md">Cancel</Text>
					</Flex>
				</Button>
				<Button
					size="sm"
					variant="primary"
					onPress={phoneNumber ? handleApprove : undefined}
				>
					<Flex align="center" gap="xs">
						<Icon name="Phone" size="xs" />
						<Text size="md">Approve call</Text>
					</Flex>
				</Button>
			</Flex>
		</Flex>
	);
};
